<script setup lang="ts">
  import { nextTick, ref, watch } from 'vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import V2CatalogCategorySheet from './V2CatalogCategorySheet.vue';

  /**
   * The categories on phones (plan §5.7): «☰ Categorías» (the full list in a
   * sheet), then one chip per category; the active one is underlined and
   * kept centred by moving the row's own scroller.
   */
  const { state, data, jumpToCategory } = useCatalogPage();
  const scroller = ref<HTMLElement | null>(null);
  const sheetOpen = ref(false);

  watch(
    () => state.activeCategoryId,
    async (id) => {
      if (id === null) return;
      await nextTick();
      const box = scroller.value;
      const chip = box?.querySelector<HTMLElement>(`[data-chip-id="${id}"]`);
      if (!box || !chip) return;
      box.scrollLeft = Math.max(
        0,
        chip.offsetLeft - (box.clientWidth - chip.offsetWidth) / 2
      );
    }
  );
</script>

<template>
  <div class="v2-catalog-chip-nav" data-tier-only="phone">
    <div ref="scroller" class="chip-scroller">
      <button
        type="button"
        class="chip-all"
        :aria-label="$t('menuPage.nav.title')"
        @click="sheetOpen = true"
      >
        <i class="mdi mdi-menu all-icon" />{{ $t('menuPage.nav.short') }}
      </button>
      <button
        v-for="section in data.shownSections.value"
        :key="section.category.id"
        type="button"
        class="chip-category"
        :class="{ 'is-active': section.category.id === state.activeCategoryId }"
        :aria-current="
          section.category.id === state.activeCategoryId ? 'true' : undefined
        "
        :data-chip-id="section.category.id"
        @click="jumpToCategory(section.category.id)"
      >
        {{ section.category.name }}
      </button>
    </div>
    <V2CatalogCategorySheet v-model="sheetOpen" />
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-chip-nav {
    @apply -tw-mx-4;

    // Relative: the chips' offsetLeft is measured from the scroller.
    .chip-scroller {
      @apply tw-relative tw-flex tw-flex-nowrap tw-items-center tw-overflow-x-auto tw-px-4;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    .chip-all {
      @apply tw-mr-2 tw-inline-flex tw-h-11 tw-flex-none tw-cursor-pointer tw-items-center tw-whitespace-nowrap tw-rounded-full tw-border-0 tw-bg-neutral-100 tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-800;

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      .all-icon {
        @apply tw-mr-1.5 tw-text-base tw-leading-none;
      }
    }

    .chip-category {
      @apply tw-relative tw-block tw-h-12 tw-flex-none tw-cursor-pointer tw-truncate tw-border-0 tw-bg-transparent tw-px-3 tw-text-sm tw-font-medium tw-leading-[48px] tw-text-neutral-500;
      max-width: 220px;

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-inset tw-ring-orange-500/40;
      }

      &.is-active {
        @apply tw-text-neutral-900;

        &::after {
          @apply tw-absolute tw-bottom-0 tw-left-3 tw-right-3 tw-h-0.5 tw-rounded-full tw-bg-neutral-900;
          content: '';
        }
      }
    }
  }
</style>
